import { Alert, Stack, Typography } from '@mui/material';
import Widget from '@/components/common/widget.tsx';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import type { PlayerDto } from '@/types/dtos/player/response/player.dto';
import type { PlayerParentEntity } from '@/types/dtos/player/player-parent-entity';
import type { LiveContextDto } from '@/types/dtos/live-contexts/live-context.dto';
import type { AvSourcesDto } from '@/types/dtos/player/response/av-sources.dto';
import { ConfigurationPlayerSection } from './configuration-player-section';
import { AvSourceSelect } from './av-source-select';

type InputsConfigurationWidgetProps = {
  mediaPlayers: PlayerDto[];
  entityId: string;
  entityType: PlayerParentEntity;
  eventSlotId?: string | null;
  liveContext?: LiveContextDto | null;
  disabled?: boolean;
  mediaPlayersDisabled?: boolean;
  avSources?: AvSourcesDto | null;
  avSelectDisabled?: boolean;
  /** Replaces {@link avSelectDisabled} for the audience device. Missing sources still disable it. */
  audienceSelectDisabled?: boolean;
  onChangeAvSources?: (next: {
    videoPlayerId: string | null;
    audioPlayerId: string | null;
    audienceVideoPlayerId?: string | null;
  }) => void;
  /** Audience cam candidates. Omit to hide the audience video select (spaces). */
  audienceCam?: { confidencePlayers: PlayerDto[]; houseCamPlayers: PlayerDto[] };
  videoPlayerId?: string | null;
  audioPlayerId?: string | null;
};

export function InputsConfigurationWidget({
  mediaPlayers,
  entityId,
  entityType,
  eventSlotId,
  liveContext,
  disabled,
  mediaPlayersDisabled,
  avSources,
  avSelectDisabled,
  audienceSelectDisabled,
  onChangeAvSources,
  videoPlayerId,
  audioPlayerId,
  audienceCam,
}: InputsConfigurationWidgetProps) {
  const t = useTranslations();
  const warnings = avSources?.warnings ?? [];
  const globalWarnings = warnings.filter((warning) => !warning.field);
  const selectablePlayers = avSources?.selectablePlayers ?? [];
  const selectsDisabled = disabled || avSelectDisabled || !avSources || !onChangeAvSources;
  const houseCamPlayer = audienceCam?.houseCamPlayers.find((player) => player.isConnected);
  const audiencePlayers = houseCamPlayer ? [houseCamPlayer] : (audienceCam?.confidencePlayers ?? []);
  const savedAudienceId = avSources?.audienceVideoPlayerId;
  const audienceMonitorId = audiencePlayers.some((player) => player.id === savedAudienceId)
    ? savedAudienceId
    : audiencePlayers[0]?.id;
  const audienceValue = houseCamPlayer?.id ?? audienceMonitorId ?? null;
  const audienceChoosable = !houseCamPlayer && audiencePlayers.length > 1;
  const audienceDisabled =
    disabled || (audienceSelectDisabled ?? avSelectDisabled) || !avSources || !onChangeAvSources || !audienceChoosable;

  return (
    <Widget title={t('flows.detail.configuration.inputs.title')} titleVariant="h6">
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('flows.detail.configuration.inputs.intro')}
        </Typography>

        {avSources?.liveLocked ? (
          <Alert severity="info">{t('flows.detail.configuration.inputs.liveLocked')}</Alert>
        ) : null}

        {globalWarnings.map((warning) => (
          <Alert key={warning.code} severity="warning">
            {t(`flows.detail.configuration.inputs.warnings.${warning.code}` as TranslationKey)}
          </Alert>
        ))}

        <ConfigurationPlayerSection
          title={t('flows.detail.configuration.inputs.mediaPlayer.title')}
          intro={t('flows.detail.configuration.inputs.mediaPlayer.intro')}
          titleInfo={t('flows.detail.configuration.inputs.mediaPlayer.introTip')}
          players={mediaPlayers}
          playerType="screenPublisher"
          entityId={entityId}
          entityType={entityType}
          eventSlotId={eventSlotId}
          liveContext={liveContext}
          disabled={disabled || mediaPlayersDisabled}
          videoPlayerId={videoPlayerId}
          audioPlayerId={audioPlayerId}
        />

        <AvSourceSelect
          field="video"
          title={t('flows.detail.configuration.inputs.roomVideoSource.title')}
          intro={t('flows.detail.configuration.inputs.roomVideoSource.intro')}
          value={avSources?.videoPlayerId ?? null}
          players={selectablePlayers}
          warnings={warnings}
          disabled={selectsDisabled}
          onChange={(videoPlayerId) =>
            onChangeAvSources?.({
              videoPlayerId,
              audioPlayerId: avSources?.audioPlayerId ?? null,
            })
          }
        />

        {audienceCam ? (
          <AvSourceSelect
            field="audience"
            title={t('flows.detail.configuration.inputs.roomVideoDevice.title')}
            intro={t('flows.detail.configuration.inputs.roomVideoDevice.intro')}
            value={audienceValue}
            players={audiencePlayers}
            warnings={[]}
            allowNone={audiencePlayers.length === 0}
            disabled={audienceDisabled}
            onChange={(audienceVideoPlayerId) =>
              onChangeAvSources?.({
                videoPlayerId: avSources?.videoPlayerId ?? null,
                audioPlayerId: avSources?.audioPlayerId ?? null,
                audienceVideoPlayerId,
              })
            }
          />
        ) : null}

        <AvSourceSelect
          field="audio"
          title={t('flows.detail.configuration.inputs.roomAudioSource.title')}
          intro={t('flows.detail.configuration.inputs.roomAudioSource.intro')}
          value={avSources?.audioPlayerId ?? null}
          players={selectablePlayers}
          warnings={warnings}
          disabled={selectsDisabled}
          onChange={(audioPlayerId) =>
            onChangeAvSources?.({
              videoPlayerId: avSources?.videoPlayerId ?? null,
              audioPlayerId,
            })
          }
        />
      </Stack>
    </Widget>
  );
}
